import { useState, useCallback, useRef } from 'react';
import type { ChatMessage, PdfExportOptions, ExportProgress } from '../types';
import { generatePdf } from '../lib/pdfGenerator';
import { trackEvent, NEBULA_EVENTS } from '../../services/analytics';

interface UsePdfExportReturn {
  exportPdf: (
    messages: ChatMessage[],
    title: string,
    options?: PdfExportOptions,
    chartImages?: string[]
  ) => Promise<void>;
  progress: ExportProgress | null;
  isExporting: boolean;
  error: string | null;
  setRenderContainer: (container: HTMLElement | null) => void;
}

export function usePdfExport(): UsePdfExportReturn {
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const renderContainerRef = useRef<HTMLElement | null>(null);

  const setRenderContainer = useCallback((container: HTMLElement | null) => {
    renderContainerRef.current = container;
  }, []);

  const exportPdf = useCallback(
    async (
      messages: ChatMessage[],
      title: string,
      options: PdfExportOptions = {},
      chartImages?: string[]
    ) => {
      if (messages.length === 0) {
        setError('No messages to export');
        return;
      }

      setIsExporting(true);
      setError(null);
      setProgress({
        current: 0,
        total: messages.length,
        status: 'preparing',
        message: 'Preparing export...',
      });

      try {
        // Track export event
        trackEvent(NEBULA_EVENTS.NEBULA_EXPORT_PDF, {
          message_count: messages.length,
          include_thinking: options.includeThinkingContent || false,
          include_citations: options.includeCitations !== false,
          page_size: options.pageSize || 'a4',
        });

        setProgress({
          current: 0,
          total: messages.length,
          status: 'rendering',
          message: 'Rendering content...',
        });

        const blob = await generatePdf(
          messages,
          title,
          options,
          (current, total, message) => {
            setProgress({
              current,
              total,
              status: 'generating',
              message,
            });
          },
          renderContainerRef.current,
          chartImages
        );

        setProgress({
          current: messages.length,
          total: messages.length,
          status: 'complete',
          message: 'Export complete!',
        });

        // Generate filename with timestamp (or use caller-provided name)
        const timestamp = new Date().toISOString().slice(0, 10);
        const sanitizedTitle = title
          .replace(/[^a-zA-Z0-9\s-]/g, '')
          .replace(/\s+/g, '_')
          .slice(0, 50);
        const filename = options.downloadFilename ?? `nebula_${sanitizedTitle}_${timestamp}.pdf`;

        // Trigger download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Clear progress after short delay
        setTimeout(() => {
          setProgress(null);
        }, 2000);
      } catch (err) {
        console.error('PDF export failed:', err);
        setError(err instanceof Error ? err.message : 'Export failed');
        setProgress({
          current: 0,
          total: messages.length,
          status: 'error',
          message: 'Export failed',
        });
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  return {
    exportPdf,
    progress,
    isExporting,
    error,
    setRenderContainer,
  };
}
